import { _isEmptyString, _isNil, _isNotBlank, _notNil } from '@/littledash';
import type { ISODate, ISODateTime, Nullable } from '@/model/Common.model';
import store from '@/store';
import { tz, TZDate } from '@date-fns/tz';
import { add, type Duration, format, formatDuration, isValid, parseISO, sub } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import { parse as tinyDurationParse } from 'tinyduration';
import { parseDuration as durationStringParse } from '@/utils/DurationDsl';

export type DurationValidationErrorReason = 'error_format' | 'error_range_min' | 'error_range_max';
export type DurationValidationResult = { valid: true } | { valid: false; reason: DurationValidationErrorReason };

export const DateRenderFormat = {
  Date: 'd MMM yyyy',
  ISODate: 'yyyy-MM-dd',
  DateDefaultWithDay: 'eeee d MMMM yyyy',
  DateWithoutYear: 'd MMM',
} as const;
export type DateRenderFormat = (typeof DateRenderFormat)[keyof typeof DateRenderFormat];

export const DateTimeRenderFormat = {
  DateTimeDefault: 'd MMM yyyy, HH:mm',
  TimeWithSeconds: 'HH:mm:ss',
  Time: 'HH:mm',
  ISODateTime: "yyyy-MM-dd'T'HH:mm:ss.SSSX",
  ISODateTimeUTC: "yyyy-MM-dd'T'HH:mm:ss'Z'",
} as const;
export type DateTimeRenderFormat = (typeof DateTimeRenderFormat)[keyof typeof DateTimeRenderFormat];

export const TimeFormat = {
  Time_24HR: 'HH:mm',
  TimeWithSeconds_24HR: 'HH:mm:ss',
  Time_12HR: 'h:mm a',
  TimeWithSeconds_12HR: 'h:mm:ss a',
} as const;
export type TimeFormat = (typeof TimeFormat)[keyof typeof TimeFormat];

export type DurationFormat = 'long' | 'short';

export interface RenderDateOptions {
  format?: DateRenderFormat;
  defaultResponse?: string;
}

export interface RenderDateTimeOptions {
  format?: DateTimeRenderFormat | DateRenderFormat;
  defaultResponse?: string;
  timezone?: string;
}

export interface RenderTimeOptions {
  format?: TimeFormat;
  defaultResponse?: string;
}

export type RenderDurationOptions = {
  format?: DurationFormat;
  defaultResponse?: string;
  zeros?: boolean;
};

export class DateUtils {
  private static InvalidDate = 'Invalid Date';
  private static InvalidTime = 'Invalid Time';
  private static InvalidDuration = 'Invalid Duration';
  static isoDatePattern = /^(?<year>\d{4})-(?<month>[0-1]\d)-(?<day>[0-3]\d)$/;
  static isoDateTimePattern =
    /^(?<year>\d{4})-(?<month>[0-1]\d)-(?<day>[0-3]\d)T(?<hour>[0-2]\d):(?<minute>[0-5]\d):(?<second>[0-5]\d)\.(?<millisecond>\d+)Z$/;
  static isoTimePattern =
    /^(?<hour>[0-1]\d|2[0-3]):(?<minute>[0-5]\d)(?::(?<second>[0-5]\d)(?:\.(?<millisecond>\d+))?)?$/;
  static isoDurationPattern =
    /^(?<sign>[+-])?P(?:(?<days>[0-9]+)D)?(?:T(?:(?<hours>[0-9]+)H)?(?:(?<minutes>[0-9]+)M)?(?:(?<seconds>[0-9]+)(?:[.,](?<nanos>[0-9]{0,9}))?S)?)?$/;

  static browserTimezone(defaultTimezone = 'UTC'): string {
    return Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ?? defaultTimezone;
  }

  static timezone(): string {
    return store.getState()?.user?.currentUser?.timezone ?? DateUtils.browserTimezone();
  }

  static formatISO(date: number): ISODateTime {
    if (typeof date === 'number') {
      return new Date(date).toISOString();
    }
    return DateUtils.InvalidDate;
  }

  static partiallyNormalizeDuration(duration: Duration): Duration {
    const normalized: Duration = { ...duration };
    // Track which fields were exactly zero in the original input (not fractional values that truncate to zero)
    const explicitZeros = new Set<string>();
    Object.entries(duration).forEach(([field, value]) => {
      if (value === 0) {
        explicitZeros.add(field);
      }
    });

    if (_notNil(normalized.days)) {
      const wholeDays = Math.trunc(normalized.days);
      const decimalDays = normalized.days - wholeDays;
      if (decimalDays !== 0) {
        normalized.days = wholeDays;
        normalized.hours = (normalized.hours ?? 0) + decimalDays * 24;
      }
    }

    if (_notNil(normalized.hours)) {
      const wholeHours = Math.trunc(normalized.hours);
      const decimalHours = normalized.hours - wholeHours;
      if (decimalHours !== 0) {
        normalized.hours = wholeHours;
        normalized.minutes = (normalized.minutes ?? 0) + decimalHours * 60;
      }
    }

    if (_notNil(normalized.minutes)) {
      const wholeMinutes = Math.trunc(normalized.minutes);
      const decimalMinutes = normalized.minutes - wholeMinutes;
      if (decimalMinutes !== 0) {
        normalized.minutes = wholeMinutes;
        normalized.seconds = (normalized.seconds ?? 0) + decimalMinutes * 60;
      }
    }

    if (_notNil(normalized.seconds)) {
      const secondsValue = normalized.seconds;
      const nearestInteger = Math.round(secondsValue);
      normalized.seconds =
        Math.abs(secondsValue - nearestInteger) < 0.0001 ? nearestInteger : Math.round(secondsValue * 1000) / 1000;
    }

    return Object.entries(normalized).reduce<Duration>((acc, [field, value]) => {
      if (!Number.isFinite(value)) {
        return acc;
      }
      // Keep non-zero values OR zero values that were explicitly zero in the original input
      if (value !== 0 || explicitZeros.has(field)) {
        return { ...acc, [field]: value };
      }
      return acc;
    }, {});
  }

  static fullyNormalizeDuration(duration: Duration): Duration {
    const normalized: Duration = {};
    const totalSeconds =
      (duration?.days ?? 0) * 86400 +
      (duration?.hours ?? 0) * 3600 +
      (duration?.minutes ?? 0) * 60 +
      (duration?.seconds ?? 0);

    if (totalSeconds === 0) {
      return normalized;
    }

    // Handle negative durations by working with absolute value
    const isNegative = totalSeconds < 0;
    const absSeconds = Math.abs(totalSeconds);

    // Extract whole units from absolute total seconds
    const days = Math.floor(absSeconds / 86400);
    const remainingAfterDays = absSeconds % 86400;

    const hours = Math.floor(remainingAfterDays / 3600);
    const remainingAfterHours = remainingAfterDays % 3600;

    const minutes = Math.floor(remainingAfterHours / 60);
    const seconds = remainingAfterHours % 60;

    // Apply sign to components
    if (days > 0) {
      normalized.days = isNegative ? -days : days;
    }
    if (hours > 0) {
      normalized.hours = isNegative ? -hours : hours;
    }
    if (minutes > 0) {
      normalized.minutes = isNegative ? -minutes : minutes;
    }
    if (seconds > 0) {
      const nearestInteger = Math.round(seconds);
      const roundedSeconds =
        Math.abs(seconds - nearestInteger) < 0.0001 ? nearestInteger : Math.round(seconds * 1000) / 1000;
      normalized.seconds = isNegative ? -roundedSeconds : roundedSeconds;
    }

    return normalized;
  }

  static normalizeDuration(duration: Duration, type: 'full' | 'partial' | 'none' = 'partial') {
    switch (type) {
      case 'full':
        return DateUtils.fullyNormalizeDuration(duration);
      case 'partial':
        return DateUtils.partiallyNormalizeDuration(duration);
      default:
        return duration;
    }
  }

  static renderDate(isoDate: Nullable<ISODate>, renderDateOption: RenderDateOptions = {}): string {
    if (_notNil(isoDate) && DateUtils.isoDatePattern.test(isoDate)) {
      const timezone = tz(DateUtils.timezone());
      const date = parseISO(isoDate, { in: timezone });
      if (isValid(date)) {
        return format(date, renderDateOption?.format ?? DateRenderFormat.Date, { in: timezone });
      }
    }
    return renderDateOption?.defaultResponse ?? DateUtils.InvalidDate;
  }

  static renderDateTime(isoDateTime: Nullable<ISODateTime>, renderDateTimeOptions: RenderDateTimeOptions = {}): string {
    if (_notNil(isoDateTime) && DateUtils.isoDateTimePattern.test(isoDateTime)) {
      const date = new TZDate(isoDateTime, renderDateTimeOptions?.timezone ?? DateUtils.timezone());
      if (isValid(date)) {
        return format(date, renderDateTimeOptions?.format ?? DateTimeRenderFormat.DateTimeDefault);
      }
    }
    return renderDateTimeOptions?.defaultResponse ?? DateUtils.InvalidDate;
  }

  static renderTime(time: Nullable<string>, options: RenderTimeOptions = {}): string {
    const dateWithTime = DateUtils.isoTimeToDate(time);
    if (isValid(dateWithTime)) {
      return format(dateWithTime, options?.format ?? TimeFormat.Time_24HR);
    }
    return options?.defaultResponse ?? DateUtils.InvalidTime;
  }

  static validDuration(duration: Nullable<string>): duration is string {
    return DateUtils.isoDurationPattern.test(duration ?? '');
  }

  static renderDuration(duration: Nullable<string>, options: RenderDurationOptions = {}): string {
    if (_notNil(duration)) {
      try {
        const parsedDuration = tinyDurationParse(duration);
        const negativePrefix = parsedDuration.negative === true ? '-' : '';
        if (options?.format === 'short') {
          const formatted = formatDuration(parsedDuration, {
            zero: options?.zeros ?? false,
            locale: {
              formatDistance: (token: string, count: number): string => {
                switch (token) {
                  case 'xYears': {
                    return `${count}yr`;
                  }
                  case 'xMonths': {
                    return `${count}mth`;
                  }
                  case 'xWeeks': {
                    return `${count}wk`;
                  }
                  case 'xDays': {
                    return `${count}d`;
                  }
                  case 'xHours': {
                    return `${count}h`;
                  }
                  case 'xMinutes': {
                    return `${count}m`;
                  }
                  case 'xSeconds': {
                    return `${count}s`;
                  }
                  default: {
                    return '';
                  }
                }
              },
            },
          });
          return _isEmptyString(formatted) ? formatted : negativePrefix + formatted;
        }
        const formatted = formatDuration(parsedDuration, { zero: options?.zeros ?? false });
        return _isEmptyString(formatted) ? formatted : negativePrefix + formatted;
      } catch (e) {
        // intentionally empty
      }
    }
    return options?.defaultResponse ?? DateUtils.InvalidDuration;
  }

  static dateTimeNow(): ISODateTime {
    return new Date().toISOString();
  }

  static dateNow(): ISODate {
    return DateUtils.formatDate(new Date());
  }

  static timezoneName(
    isoDateTime: Nullable<ISODateTime>,
    timeZone: string,
    type: 'short' | 'long' | 'shortOffset' | 'longOffset' | 'shortGeneric' | 'longGeneric' = 'long'
  ): string | undefined {
    return new Intl.DateTimeFormat(window.navigator.languages, { timeZone, timeZoneName: type })
      .formatToParts(_isNil(isoDateTime) ? new Date() : new Date(isoDateTime))
      .find((part) => part.type === 'timeZoneName')?.value;
  }

  static timezoneAbbreviation(isoDateTime: Nullable<ISODateTime>, timezone: string): string {
    return formatInTimeZone(_isNil(isoDateTime) ? new Date() : new Date(isoDateTime), timezone, 'zzz');
  }

  static timezoneOffsetMilliseconds(isoDateTime: Nullable<ISODateTime>, timeZone: string): number {
    return getTimezoneOffset(timeZone, _isNil(isoDateTime) ? new Date() : new Date(isoDateTime));
  }

  static formatDate(date: Date): ISODate {
    return format(date, DateRenderFormat.ISODate);
  }

  static formatISODateTimeToDate(isoDateTime: ISODateTime): ISODate {
    if (_isNil(isoDateTime) || _isEmptyString(isoDateTime)) {
      return DateUtils.InvalidDate;
    }
    return format(isoDateTime, DateRenderFormat.ISODate, { in: tz(DateUtils.timezone()) });
  }

  static roundDate(date: Date, by: 'seconds' | 'minutes' | '30minutes' = 'seconds'): Date {
    switch (by) {
      case 'seconds':
        date.setMilliseconds(date.getMilliseconds() >= 500 ? 1000 : 0);
        break;
      case 'minutes': {
        date.setMilliseconds(date.getMilliseconds() >= 500 ? 1000 : 0);
        date.setSeconds(date.getSeconds() >= 30 ? 60 : 0, 0);
        break;
      }
      case '30minutes': {
        date.setMilliseconds(date.getMilliseconds() >= 500 ? 1000 : 0);
        date.setSeconds(date.getSeconds() >= 30 ? 60 : 0, 0);
        date.setMinutes(Math.round(date.getMinutes() / 30) * 30, 0, 0);
        break;
      }
    }
    return date;
  }

  static isValidDate(isoDate: Nullable<ISODate>): isoDate is string & boolean {
    return _notNil(isoDate) && DateUtils.isoDatePattern.test(isoDate) && isValid(toZonedTime(isoDate, 'UTC'));
  }

  static validatorWithCallback() {
    return (value: unknown, callback: (valid: boolean) => void) => {
      if (_isNil(value) || _isEmptyString(value, false)) {
        callback(true);
        return;
      }

      const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
      callback(DateUtils.isValidDate(stringValue));
    };
  }

  static isValidDateTime(isoDateTime: Nullable<ISODateTime>): isoDateTime is string & boolean {
    return (
      _notNil(isoDateTime) &&
      DateUtils.isoDateTimePattern.test(isoDateTime) &&
      isValid(toZonedTime(isoDateTime, DateUtils.timezone()))
    );
  }

  static convertSecondsToUnit(seconds: number, unit: 'MINUTES' | 'HOURS' | 'DAYS'): number {
    switch (unit) {
      case 'MINUTES':
        return seconds / 60;
      case 'HOURS':
        return seconds / 3600;
      case 'DAYS':
        return seconds / 86400;
    }
    return seconds;
  }

  static convertUnitToSeconds(seconds: number, unit: 'MINUTES' | 'HOURS' | 'DAYS'): number {
    switch (unit) {
      case 'MINUTES':
        return seconds * 60;
      case 'HOURS':
        return seconds * 3600;
      case 'DAYS':
        return seconds * 86400;
    }
    return seconds;
  }

  static startOfDay(isoDate: ISODate, options: { timezone?: string } = {}): ISODateTime {
    if (DateUtils.isValidDate(isoDate)) {
      return DateUtils.formatISO(
        parseISO(`${isoDate}T00:00:00.000`, { in: tz(options?.timezone ?? DateUtils.timezone()) }).getTime()
      );
    }
    return 'Invalid date';
  }

  static endOfDay(isoDate: ISODate): ISODateTime {
    if (DateUtils.isValidDate(isoDate)) {
      return DateUtils.formatISO(parseISO(`${isoDate}T23:59:59.999`, { in: tz(DateUtils.timezone()) }).getTime());
    }
    return 'Invalid date';
  }

  static addOffsetToISODate(isoDate: ISODate, offset: string) {
    if (_isNil(isoDate) || !DateUtils.isValidDate(isoDate)) {
      return 'Invalid date';
    }
    try {
      const {
        years = 0,
        months = 0,
        weeks = 0,
        days = 0,
        hours = 0,
        minutes = 0,
        seconds = 0,
        negative,
      } = tinyDurationParse(offset);

      // Calendar date arithmetic in UTC so the result doesn't depend on the browser timezone or DST transitions
      const utc = tz('UTC');
      const date = parseISO(isoDate, { in: utc });
      const updatedDate =
        negative === true
          ? sub(date, { years, months, weeks, days, hours, minutes, seconds }, { in: utc })
          : add(date, { years, months, weeks, days, hours, minutes, seconds }, { in: utc });

      return format(updatedDate, 'yyyy-MM-dd', { in: utc });
    } catch {
      return 'Invalid offset';
    }
  }

  static isoTimeToDate(time: Nullable<string>): Date {
    const match = DateUtils.isoTimePattern.exec(time ?? '');
    return new Date(
      0,
      0,
      0,
      Number(match?.groups?.hour ?? '0'),
      Number(match?.groups?.minute ?? '0'),
      Number(match?.groups?.second ?? '0'),
      Number(match?.groups?.millisecond ?? '0')
    );
  }

  static createDateTimeInUserTimezone(dateInput: ISODate | ISODateTime, hoursAndMinutes: string): ISODateTime {
    // Extract just the date part if it's a full datetime
    const datePart = dateInput.includes('T') ? dateInput.split('T')[0] : dateInput;
    const localDateTime = `${datePart}T${hoursAndMinutes}:00.000`;
    const utcDateTime = isValid(new Date(localDateTime))
      ? DateUtils.formatISO(parseISO(localDateTime, { in: tz(DateUtils.timezone()) }).getTime())
      : '';
    if (DateUtils.isValidDateTime(utcDateTime)) {
      return utcDateTime;
    }
    return '';
  }

  static async parseDuration(
    value: Nullable<string>,
    options: { normalize?: 'full' | 'partial' | 'none'; defaultUnit?: 'days' | 'hours' | 'minutes' | 'seconds' } = {}
  ): Promise<string> {
    const rawDuration = (value ?? '').toUpperCase();

    if (DateUtils.validDuration(rawDuration)) {
      return Promise.resolve(
        DateUtils.formatDuration(DateUtils.normalizeDuration(tinyDurationParse(rawDuration), options?.normalize))
      );
    }
    return durationStringParse(rawDuration, options?.defaultUnit ?? 'hours').then((duration) =>
      DateUtils.formatDuration(DateUtils.normalizeDuration(duration, options?.normalize))
    );
  }

  static formatDuration(value: Duration): string {
    const values = Object.values(value);
    const negative =
      (value as { negative?: boolean }).negative === true ||
      (values.length > 0 && values.some((v) => Number.isFinite(v) && v < 0));
    const components = [negative ? '-P' : 'P'];
    if (Number.isFinite(value.years)) {
      components.push(`${negative ? Math.abs(value.years as number) : value.years}Y`);
    }
    if (Number.isFinite(value.months)) {
      components.push(`${negative ? Math.abs(value.months as number) : value.months}M`);
    }
    if (Number.isFinite(value.weeks)) {
      components.push(`${negative ? Math.abs(value.weeks as number) : value.weeks}W`);
    }
    if (Number.isFinite(value.days)) {
      components.push(`${negative ? Math.abs(value.days as number) : value.days}D`);
    }
    if (Number.isFinite(value.hours) || Number.isFinite(value.minutes) || Number.isFinite(value.seconds)) {
      components.push('T');
    }
    if (Number.isFinite(value.hours)) {
      components.push(`${negative ? Math.abs(value.hours as number) : value.hours}H`);
    }
    if (Number.isFinite(value.minutes)) {
      components.push(`${negative ? Math.abs(value.minutes as number) : value.minutes}M`);
    }
    if (Number.isFinite(value.seconds)) {
      components.push(`${negative ? Math.abs(value.seconds as number) : value.seconds}S`);
    }
    return components.join('');
  }

  static validateDurationRange(
    isoDuration: Nullable<string>,
    options: { minSeconds?: number; maxSeconds?: number; allow_zero?: boolean; allow_negative?: boolean } = {}
  ): DurationValidationResult {
    const min = options.minSeconds ?? 60;
    const max = options.maxSeconds ?? 365 * 86400;

    try {
      const parsedDuration = tinyDurationParse(isoDuration ?? '');
      const totalSeconds =
        (parsedDuration?.days ?? 0) * 86400 +
        (parsedDuration?.hours ?? 0) * 3600 +
        (parsedDuration?.minutes ?? 0) * 60 +
        (parsedDuration?.seconds ?? 0);

      if (totalSeconds === 0 && options.allow_zero === true) {
        return { valid: true };
      }
      if (parsedDuration.negative === true && options.allow_negative !== true) {
        return { valid: false, reason: 'error_range_min' };
      }
      const absSeconds = Math.abs(totalSeconds);
      if (absSeconds < min) {
        return { valid: false, reason: 'error_range_min' };
      }
      if (absSeconds > max) {
        return { valid: false, reason: 'error_range_max' };
      }
      return { valid: true };
    } catch (e) {
      return { valid: false, reason: 'error_format' };
    }
  }
}

export class DateInputUtils {
  private static localDateTimePattern =
    /^(?<year>\d{4})-(?<month>[0-1]\d)-(?<day>[0-3]\d)T(?<hour>[0-2]\d):(?<minute>[0-5]\d)[0-9:.]{0,7}$/;
  private static localDateTimeNormalizationPattern = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/;
  static hotDatePattern = 'YYYY-MM-DD';

  static toLocalDateTime(isoDateTime: Nullable<ISODateTime>, options?: { includeSeconds?: boolean }): string | '' {
    if (_notNil(isoDateTime) && DateUtils.isoDateTimePattern.test(isoDateTime)) {
      const date = new Date(isoDateTime);
      const timezone = tz(DateUtils.timezone());
      if (isValid(date)) {
        return format(date, `yyyy-MM-dd'T'HH:mm${(options?.includeSeconds ?? true) ? ':ss' : ''}`, {
          in: timezone,
        });
      }
    }
    return '';
  }

  static localDateTimeToISODateTime(localDateTime: string): ISODateTime | '' {
    if (DateInputUtils.localDateTimePattern.test(localDateTime)) {
      // Parse the datetime-local input as being in the user's timezone
      // by treating it as a local time in that timezone
      const userTimezone = DateUtils.timezone();

      // Normalize datetime-local format to full ISO string for parseISO
      // Handles "YYYY-MM-DDTHH:MM", "YYYY-MM-DDTHH:MM:SS", and "YYYY-MM-DDTHH:MM:SS.sss" formats
      const isoString = localDateTime.replace(
        DateInputUtils.localDateTimeNormalizationPattern,
        (match, dateTime, seconds, fractional) => {
          const normalizedSeconds = seconds ?? '00';
          const normalizedFractional = typeof fractional === 'string' ? fractional.padEnd(3, '0').slice(0, 3) : '000';
          return `${dateTime}:${normalizedSeconds}.${normalizedFractional}`;
        }
      );

      const dateTimeInUserTz = parseISO(isoString, { in: tz(userTimezone) });
      if (isValid(dateTimeInUserTz)) {
        return DateUtils.formatISO(dateTimeInUserTz.getTime());
      }
    }
    return '';
  }
}

export class TimeUtils {
  static isValidTime(time: string, format: TimeFormat): boolean {
    const match = time.match(DateUtils.isoTimePattern);
    if (_isNil(match?.groups)) {
      return false;
    }

    const { hour, minute, second } = match.groups;

    switch (format) {
      case TimeFormat.Time_24HR:
        return Boolean(hour) && Boolean(minute);
      case TimeFormat.TimeWithSeconds_24HR:
        return Boolean(hour) && Boolean(minute) && Boolean(second);
    }
    return false;
  }

  static currentTimeInUserTimezone(): string {
    return format(DateUtils.dateTimeNow(), TimeFormat.Time_24HR);
  }
}

export class DateTestUtils {
  static generateRandomDate(
    start: Readonly<Date> = new Date(-8640000000000000),
    end: Readonly<Date> = new Date(8640000000000000)
  ): Date {
    return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  }

  static randomDate(
    start: Readonly<Date> = new Date(-8640000000000000),
    end: Readonly<Date> = new Date(8640000000000000)
  ): ISODate {
    return format(DateTestUtils.generateRandomDate(start, end), DateRenderFormat.ISODate);
  }

  static randomDateTime(
    start: Readonly<Date> = new Date(-8640000000000000),
    end: Readonly<Date> = new Date(8640000000000000)
  ): ISODateTime {
    return DateTestUtils.generateRandomDate(start, end).toISOString();
  }
}
